import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/authContext';
import API from '../../utils/API';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
} from '@mui/material';
import { ArrowLeft, Upload, Save } from 'lucide-react';

export default function CreateEvent() {
  const { showrunner } = useAuth();
  const navigate = useNavigate();

  const [newProd, setNewProd] = useState({
    name: "",
    poster_portrait: null,
  });
  const [newProdDesc, setNewProdDesc] = useState("");
  const [newShow, setNewShow] = useState({
    date: "",
    theater: "",
    location: "",
    venue: "",
    private: false,
  });
  const [theaters, setTheaters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    API.getTheaters().then(res => {
      console.log("Theaters", res.data)
      setTheaters(res.data);
    });
  }, []);

  function handleNewShowChange(e) {
    const { name, value, checked } = e.target;
    if (name === "private") {
      setNewShow(prev => ({ ...prev, [name]: checked }));
    } else {
      setNewShow(prev => ({ ...prev, [name]: value }));
    }
  }

  function handleNewProdChange(e) {
    const { name, type, value, files } = e.target;
    if (!e.target) return;
    if (type === "file") {
      setNewProd(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setNewProd(prev => ({ ...prev, [name]: value }));
    }
  }

  function addEvent() {
    setLoading(true);
    setError('');
    
    const req = new FormData();
    Object.keys(newProd).forEach(key => {
      req.append(key, newProd[key]);
    });
    req.append('users', showrunner.id);
    req.append('description', newProdDesc);
    req.append("stripe_connect_id", showrunner.stripe_connect_id);

    // Add show data to FormData
    Object.keys(newShow).forEach(key => {
      let value = newShow[key];
      if (typeof value === 'boolean') {
        value = value ? "True" : "False";
      }
      req.append(key, value);
    });

    if (!newProd.poster_portrait) {
      setError("Please upload a poster");
      setLoading(false);
      return;
    }
    if (!newProd.name) {
      setError("Please enter a title");
      setLoading(false);
      return;
    }
    
    API.addEvent(req).then(res => {
      setError('');
      setLoading(false);
      const { theater, production, id } = res.data.show;

      // Navigate to the new show management page with state
      navigate({
        pathname: '/showrunner/show',
        search: `?theater=${encodeURIComponent(theater)}&production=${encodeURIComponent(production)}&showID=${id}`
      });
    }).catch(err => {
      if (err.response.data) {
        if (err.response.data.poster_portrait) {
          setError(err.response.data.poster_portrait[0]);
        }
      }
      setError("Failed to create new event.");
      setLoading(false);
    });
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #F8FAFC 100%)',
        borderRadius: 3,
        p: 4,
        mb: 4,
        border: '1px solid #E2E8F0',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/showrunner/events')}
            sx={{ 
              minWidth: 'auto',
              borderRadius: 2,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white',
                borderColor: 'primary.main',
              }
            }}
          >
            Back to Events
          </Button>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" fontWeight="700" sx={{ mb: 1, color: 'grey.800' }}>
              Create New Event
            </Typography>
            <Typography variant="h6" color="grey.600" fontWeight="400">
              Set up your event details and start selling tickets to your audience
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Left Column - Form */}
        <Grid item xs={12} lg={8}>
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            border: '1px solid #E2E8F0',
            '&:hover': {
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
            },
            transition: 'all 0.3s ease-in-out'
          }}>
            <CardContent sx={{ p: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.25rem'
                }}>
                  📝
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="700" sx={{ color: 'grey.800' }}>
                    Event Information
                  </Typography>
                  <Typography variant="body2" color="grey.600">
                    Tell us about your event
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={3}>
                {/* Event Title */}
                <Grid item xs={12}>
                  <TextField
                    label="Event Title"
                    placeholder="Enter your event name"
                    name="name"
                    value={newProd.name}
                    onChange={handleNewProdChange}
                    fullWidth
                    required
                    variant="outlined"
                  />
                </Grid>

                {/* Venue and Location */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Venue"
                    placeholder="e.g., Grand Theater"
                    name="venue"
                    value={newShow.venue}
                    onChange={handleNewShowChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Address"
                    placeholder="e.g., 123 Main St, City, State"
                    name="location"
                    value={newShow.location}
                    onChange={handleNewShowChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>

                {/* Date and Time */}
                <Grid item xs={12}>
                  <TextField
                    label="Date and Time"
                    type="datetime-local"
                    name="date"
                    value={newShow.date}
                    onChange={handleNewShowChange}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    placeholder="Describe your event..."
                    multiline
                    rows={4}
                    value={newProdDesc}
                    onChange={(e) => setNewProdDesc(e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>

                {/* Floorplan Selection */}
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Floorplan</InputLabel>
                    <Select
                      name="theater"
                      value={newShow.theater}
                      onChange={handleNewShowChange}
                      label="Floorplan"
                    >
                      <MenuItem value="">None</MenuItem>
                      {theaters.map(theater => (
                        <MenuItem key={theater.id} value={theater.name}>
                          {theater.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Private Event Toggle */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="private"
                        checked={newShow.private}
                        onChange={handleNewShowChange}
                        color="primary"
                      />
                    }
                    label="Make this event private"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Poster Upload */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Poster Upload */}
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              border: '1px solid #E2E8F0',
              '&:hover': {
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              },
              transition: 'all 0.3s ease-in-out'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #FF6B35 0%, #FF8A65 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1rem'
                  }}>
                    🖼️
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="700" sx={{ color: 'grey.800' }}>
                      Event Poster
                    </Typography>
                    <Typography variant="body2" color="grey.600">
                      Upload your event image
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ 
                  textAlign: 'center',
                  border: '2px dashed #E2E8F0',
                  borderRadius: 2,
                  p: 4,
                  backgroundColor: '#FAFBFC',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: '#4A5BFF',
                    backgroundColor: '#F8FAFF',
                  }
                }}>
                  <input
                    type="file"
                    id="poster-upload"
                    accept="image/*"
                    onChange={handleNewProdChange}
                    name="poster_portrait"
                    style={{ display: 'none' }}
                  />
                  
                  <Button
                    component="label"
                    htmlFor="poster-upload"
                    variant="contained"
                    startIcon={<Upload size={18} />}
                    sx={{
                      background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
                      borderRadius: 2,
                      py: 1.5,
                      px: 3,
                      fontWeight: 600,
                      mb: 2,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #001ECC 0%, #0029FF 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(0, 41, 255, 0.3)',
                      },
                      transition: 'all 0.3s ease-in-out'
                    }}
                  >
                    Upload Poster
                  </Button>
                  
                  {newProd.poster_portrait && (
                    <Box sx={{ 
                      mt: 2,
                      p: 2,
                      backgroundColor: '#E8F5E8',
                      borderRadius: 2,
                      border: '1px solid #00C851'
                    }}>
                      <Typography variant="body2" color="success.main" fontWeight="600">
                        ✓ {newProd.poster_portrait.name}
                      </Typography>
                    </Box>
                  )}
                  
                  <Typography variant="caption" color="grey.600" sx={{ mt: 2, display: 'block' }}>
                    Portrait orientation recommended • Max 10MB
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Create Button */}
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              border: '1px solid #E2E8F0',
              background: 'linear-gradient(135deg, #ffffff 0%, #F8FAFC 100%)',
            }}>
              <CardContent sx={{ p: 4 }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<Save size={18} />}
                  onClick={addEvent}
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
                    fontWeight: 700,
                    py: 2.5,
                    fontSize: '1.1rem',
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 8px 25px rgba(0, 41, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #001ECC 0%, #0029FF 100%)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 12px 35px rgba(0, 41, 255, 0.4)',
                    },
                    '&:disabled': {
                      background: '#CBD5E0',
                      color: '#718096',
                      transform: 'none',
                      boxShadow: 'none',
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  {loading ? 'Creating Event...' : '🚀 Create Event'}
                </Button>
                
                <Box sx={{ 
                  mt: 3, 
                  p: 2, 
                  backgroundColor: '#F0F9FF',
                  borderRadius: 2,
                  border: '1px solid #E0F2FE'
                }}>
                  <Typography variant="caption" color="grey.700" sx={{ display: 'block', textAlign: 'center', fontWeight: 500 }}>
                    ✨ You'll be able to add tickets and pricing after creation
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
